/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you are an app developer and requires the theme to re-render the mini-cart, you can trigger your own event. If
 * you are adding a product, you need to trigger the "product:added" event, and make sure that you pass the quantity
 * that was added so the theme can properly update the quantity:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('product:added', {
 *   bubbles: true,
 *   detail: {
 *     quantity: 1
 *   }
 * }));
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

(function initializeUsTariffNotice() {
  const displayNames = typeof Intl !== 'undefined' && Intl.DisplayNames
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null;

  function countryFlag(country) {
    const code = country === 'USA' ? 'US' : country;
    if (!/^[A-Z]{2}$/.test(code)) return '';
    return String.fromCodePoint(...code.split('').map(function(character) {
      return 127397 + character.charCodeAt(0);
    }));
  }

  function countryName(country) {
    const code = country === 'USA' ? 'US' : country;
    try { return displayNames ? displayNames.of(code) : code; }
    catch (error) { return code; }
  }

  function update(variantId) {
    if (!variantId) return;
    const normalizedId = String(variantId);
    document.querySelectorAll('script[data-variant-country-origin-map]').forEach(function(script) {
      let map;
      try {
        map = JSON.parse(script.textContent || '{}');
      } catch (error) {
        console.error('Could not parse variant country-of-origin map:', error);
        return;
      }
      if (!Object.prototype.hasOwnProperty.call(map, normalizedId)) return;
      const productId = String(script.dataset.productId || '');
      const countries = String(map[normalizedId] || '').split(',').map(function(country) {
        return country.trim().toUpperCase();
      }).filter(function(country) {
        return country && country !== 'NULL' && country !== 'NONE';
      });
      const discontinuedScript = Array.from(document.querySelectorAll('script[data-variant-discontinued-map]'))
        .find(function(candidate) { return String(candidate.dataset.productId || '') === productId; });
      let discontinued = false;
      if (discontinuedScript) {
        try {
          const discontinuedMap = JSON.parse(discontinuedScript.textContent || '{}');
          discontinued = discontinuedMap[normalizedId] === true;
        } catch (error) {
          console.error('Could not parse discontinued variant map:', error);
        }
      }
      document.querySelectorAll('[data-us-tariff-notice]').forEach(function(notice) {
        if (productId && String(notice.dataset.productId || '') !== productId) return;
        notice.hidden = discontinued || !countries.some(function(country) { return country === 'US' || country === 'USA'; });
      });
      document.querySelectorAll('[data-country-origin]').forEach(function(origin) {
        if (productId && String(origin.dataset.productId || '') !== productId) return;
        const flag = origin.querySelector('[data-country-origin-flag]');
        const name = origin.querySelector('[data-country-origin-name]');
        if (flag) flag.textContent = countries.map(countryFlag).filter(Boolean).join(' ');
        if (name) name.textContent = countries.map(countryName).join(' & ');
        origin.hidden = countries.length === 0;
      });
    });
  }

  function initializeProductCardCountries(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-product-card-country-origin]').forEach(function(origin) {
      const countries = String(origin.dataset.countryCodes || origin.dataset.countryCode || '').split(',').map(function(country) {
        return country.trim().toUpperCase();
      }).filter(function(country) {
        return country && country !== 'NULL' && country !== 'NONE';
      });
      const flag = origin.querySelector('[data-country-origin-flag]');
      const name = origin.querySelector('[data-country-origin-name]');
      if (flag) flag.textContent = countries.map(countryFlag).filter(Boolean).join(' ');
      if (name) name.textContent = countries.map(countryName).join(' & ');
      origin.hidden = countries.length === 0;
    });
  }

  document.addEventListener('variant:changed', function(event) {
    update(event.detail && event.detail.variant && event.detail.variant.id);
  });

  function initialize() {
    document.querySelectorAll('script[data-variant-country-origin-map]').forEach(function(script) {
      update(script.dataset.selectedVariantId);
    });
    initializeProductCardCountries(document);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize);
  else initialize();

  new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      mutation.addedNodes.forEach(function(node) {
        if (node.nodeType !== 1) return;
        if (node.matches && node.matches('[data-product-card-country-origin]')) {
          initializeProductCardCountries(node.parentElement || node);
        } else {
          initializeProductCardCountries(node);
        }
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();

/**
 * Variant ingredients
 *
 * Shopify's standard variant JSON does not include variant metafields, so the
 * product snippet outputs a separate JSON map using info.ingredients.
 */
(function initializeVariantIngredients() {
  function parseIngredientsMap(script) {
    if (script.__variantIngredientsData) return script.__variantIngredientsData;

    try {
      script.__variantIngredientsData = JSON.parse(script.textContent);
      return script.__variantIngredientsData;
    } catch (error) {
      console.error('Could not parse variant ingredients map:', error);
      return null;
    }
  }

  function updateVariantIngredients(variantId) {
    if (!variantId) return;

    const normalizedVariantId = String(variantId);

    document.querySelectorAll('script[data-variant-ingredients-map]').forEach(function(script) {
      const data = parseIngredientsMap(script);
      if (!data || !data.variants) return;

      if (!Object.prototype.hasOwnProperty.call(data.variants, normalizedVariantId)) return;

      const ingredients = String(data.variants[normalizedVariantId] || '').trim();
      const productId = String(data.productId || script.dataset.productId || '');

      document.querySelectorAll('[data-variant-ingredients]').forEach(function(container) {
        if (productId && String(container.dataset.productId || '') !== productId) return;

        const valueElement = container.querySelector('[data-variant-ingredients-value]');
        if (valueElement) valueElement.textContent = ingredients;

        container.dataset.currentVariantId = normalizedVariantId;
        container.hidden = ingredients.length === 0;
      });
    });
  }

  document.addEventListener('variant:changed', function(event) {
    const variant = event.detail && event.detail.variant;
    if (!variant || !variant.id) return;

    updateVariantIngredients(variant.id);
  });
})();

/* Persistent collection warehouse filters. Shopify filter URL parameters remain
   the source of truth so pagination, browser history, and shared URLs work. */
(function () {
  const FILTERS = [
    'filter.v.m.custom.shipping_availability',
    'filter.v.m.custom.pickup_availability'
  ];
  const STORAGE_PREFIX = 'forma.collectionAvailability.v2.';
  const PICKUP_STORAGE_KEY = 'forma_preferred_pickup_location_v1';

  function collectionFiltersDisabled() {
    return Boolean(document.querySelector('[data-disable-collection-filters]'));
  }

  function metaobjectValue(value) {
    if (value && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'value')) return value.value;
    return value;
  }

  function storefrontLocations() {
    const rawLocations = Array.isArray(window.storeDetails)
      ? window.storeDetails
      : Object.values(window.storeDetails || {});

    return rawLocations.map(raw => {
      const id = String(metaobjectValue(raw.ids) || metaobjectValue(raw.location_id) || '').split('/').pop();
      const name = String(metaobjectValue(raw.name) || '').trim();
      const province = String(metaobjectValue(raw.province_code) || '').trim().toUpperCase();
      const label = province && !new RegExp(`,\\s*${province}$`, 'i').test(name) ? `${name}, ${province}` : name;
      return {
        id,
        name,
        label,
        lat: metaobjectValue(raw.latitude) || null,
        lng: metaobjectValue(raw.longitude) || null
      };
    }).filter(location => location.id && location.name);
  }

  async function setPreferredPickupFromFilter(locationLabel) {
    if (!locationLabel) return;
    const normalizedLabel = cleanAvailabilityLabel(locationLabel, FILTERS[1]).trim().toLowerCase();
    const location = storefrontLocations().find(item =>
      item.label.toLowerCase() === normalizedLabel || item.name.toLowerCase() === normalizedLabel
    );
    if (!location) {
      console.warn(`Could not match pickup filter location: ${locationLabel}`);
      return;
    }

    localStorage.setItem(PICKUP_STORAGE_KEY, JSON.stringify({
      id: String(location.id),
      name: location.name,
      lat: location.lat,
      lng: location.lng,
      timestamp: Date.now()
    }));

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: {
          preferred_pickup_location_id: String(location.id),
          preferred_pickup_location_name: location.name
        }
      })
    });
    if (!response.ok) throw new Error(`Cart location update failed (${response.status})`);

    document.dispatchEvent(new CustomEvent('forma:preferred-pickup-location-changed', {
      detail: { location }
    }));
  }

  function addAvailabilityToCollectionLink(anchor) {
    if (!anchor?.href) return;
    const url = new URL(anchor.href, window.location.origin);
    if (url.origin !== window.location.origin || !/^\/collections(?:\/|$)/.test(url.pathname)) return;
    if (collectionFiltersDisabled() && url.pathname === window.location.pathname) return;

    FILTERS.forEach(param => {
      if (url.searchParams.has(param)) return;
      const stored = localStorage.getItem(STORAGE_PREFIX + param);
      if (stored) url.searchParams.set(param, stored);
    });
    anchor.href = url.toString();
  }

  function decorateCollectionLinks(root) {
    (root || document).querySelectorAll('a[href*="/collections/"]').forEach(addAvailabilityToCollectionLink);
  }

  function storedAvailabilityValue(param) {
    return localStorage.getItem(STORAGE_PREFIX + param) || '';
  }

  function addSavedAvailabilityToSearchForm(form) {
    if (!form || String(form.method || 'get').toLowerCase() !== 'get') return;
    const action = new URL(form.action || window.location.href, window.location.origin);
    if (action.origin !== window.location.origin || action.pathname !== '/search') return;

    FILTERS.forEach(param => {
      if (form.elements.namedItem(param)) return;
      const stored = storedAvailabilityValue(param);
      if (!stored) return;

      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = param;
      input.value = stored;
      input.dataset.savedAvailabilityFilter = 'true';
      form.appendChild(input);
    });
  }

  function applySavedAvailabilityToCurrentSearch() {
    const url = new URL(window.location.href);
    if (url.pathname !== '/search' || !url.searchParams.has('q')) return false;

    let changed = false;
    FILTERS.forEach(param => {
      if (url.searchParams.has(param)) return;
      const stored = storedAvailabilityValue(param);
      if (!stored) return;
      url.searchParams.set(param, stored);
      changed = true;
    });

    if (changed) {
      window.location.replace(url.toString());
      return true;
    }
    return false;
  }

  function cleanAvailabilityLabel(value, param) {
    const prefix = param.includes('shipping_') ? 'Shipping available from ' : 'Available at ';
    return String(value || '').replace(prefix, '');
  }

  function syncAvailabilitySelectors(root) {
    const url = new URL(window.location.href);
    (root || document).querySelectorAll('[data-availability-filter]').forEach(select => {
      const param = select.dataset.filterParam;
      const value = url.searchParams.get(param) || '';
      if (value && !Array.from(select.options).some(option => option.value === value)) {
        select.add(new Option(cleanAvailabilityLabel(value, param), value, true, true));
      }
      select.value = value;
    });
  }

  async function explainAvailabilityEmptyState(root) {
    const message = (root || document).querySelector('[data-availability-empty-state]');
    if (!message || message.dataset.availabilityChecked === 'true') return;
    message.dataset.availabilityChecked = 'true';

    const currentUrl = new URL(window.location.href);
    const shipping = currentUrl.searchParams.get(FILTERS[0]);
    const pickup = currentUrl.searchParams.get(FILTERS[1]);
    if (!shipping && !pickup) return;

    const comparisonUrl = new URL(currentUrl);
    FILTERS.forEach(param => comparisonUrl.searchParams.delete(param));
    comparisonUrl.searchParams.delete('page');
    const section = document.querySelector('[data-section-type="collection"]');
    if (section?.dataset.sectionId) comparisonUrl.searchParams.set('section_id', section.dataset.sectionId);

    try {
      const response = await fetch(comparisonUrl.toString(), { credentials: 'same-origin' });
      const html = await response.text();
      const comparison = document.createElement('div');
      comparison.innerHTML = html;
      if (!comparison.querySelector('.product-item')) return;

      const pickupName = cleanAvailabilityLabel(pickup, FILTERS[1]);
      const shippingName = cleanAvailabilityLabel(shipping, FILTERS[0]);
      const resultType = message.dataset.availabilityContext === 'search' ? 'search results' : 'products in this collection';
      if (pickup && shipping) {
        message.textContent = `No ${resultType} are currently available for pickup at ${pickupName} and shipping from ${shippingName}.`;
      } else if (pickup) {
        message.textContent = `No ${resultType} are currently available for pickup at ${pickupName}.`;
      } else {
        message.textContent = `No ${resultType} are currently available to ship from ${shippingName}.`;
      }
    } catch (error) {
      /* Keep Shopify's generic empty-state text if the comparison cannot load. */
    }
  }

  function refreshAvailabilityUI(root) {
    if (collectionFiltersDisabled()) return;
    decorateCollectionLinks(root);
    syncAvailabilitySelectors(root);
    explainAvailabilityEmptyState(root);
  }

  document.addEventListener('change', async event => {
    const select = event.target.closest('[data-availability-filter]');
    if (!select) return;

    const param = select.dataset.filterParam;
    const url = new URL(window.location.href);
    url.searchParams.delete(param);
    url.searchParams.delete('page');

    if (select.value) {
      url.searchParams.set(param, select.value);
      localStorage.setItem(STORAGE_PREFIX + param, select.value);
    } else {
      localStorage.removeItem(STORAGE_PREFIX + param);
    }

    if (param === FILTERS[1] && select.value) {
      try {
        await setPreferredPickupFromFilter(select.value);
      } catch (error) {
        console.error('Could not update the preferred pickup location:', error);
      }
    }

    window.location.assign(url.toString());
  });

  document.addEventListener('click', event => {
    const clearButton = event.target.closest(
      '.collection__clear-filter, .collection__mobile-active-clear, .empty-state__button[data-action="clear-filters"]'
    );
    if (!clearButton) return;
    FILTERS.forEach(param => localStorage.removeItem(STORAGE_PREFIX + param));
  });

  document.addEventListener('click', event => {
    const anchor = event.target.closest('a[href]');
    if (anchor) addAvailabilityToCollectionLink(anchor);
  }, true);

  document.addEventListener('submit', event => {
    addSavedAvailabilityToSearchForm(event.target);
  }, true);

  function initializeAvailabilityUI() {
    if (applySavedAvailabilityToCurrentSearch()) return;
    refreshAvailabilityUI(document);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeAvailabilityUI);
  else initializeAvailabilityUI();

  new MutationObserver(mutations => {
    mutations.forEach(mutation => mutation.addedNodes.forEach(node => {
      if (node.nodeType !== 1) return;
      if (node.matches?.('a[href*="/collections/"]')) addAvailabilityToCollectionLink(node);
      refreshAvailabilityUI(node);
    }));
  }).observe(document.documentElement, { childList: true, subtree: true });
})();

document.addEventListener('variant:changed', function(event) {
  var variant = event.detail.variant;

  try {
    const bundleElements = document.querySelectorAll('.bundle-variant-container')
    if(bundleElements && bundleElements.length){
      bundleElements.forEach(element => {
        element.classList.add('hidden')
      });
      const matchingElement = document.getElementById(`bundle-container--${variant.id}`)
      if(matchingElement) matchingElement.classList.remove('hidden')
    }
  } catch (error) {
    console.error(error)
  }
});

document.addEventListener('variant:changed', function (event) {
  const variant = event.detail.variant;
  const section = document.querySelector('.product_bundles');
  if (!section) return;

  const container = section.querySelector('[data-bundle-container]');
  if (!container) return;

  const data = window.bundleData?.[section.dataset.sectionId];
  if (!data) return;

  const components = data.variants?.[variant.id]?.components || [];

  if (!components.length) {
    section.style.display = 'none';
    return;
  }

  section.style.display = '';

  container.innerHTML = components.map(item => {
    return `
      <a href="/products/${item.productHandle}" class="bundle_component_card">
        <div class="bundle_component_img">
          ${item.qty > 1 ? `<span class="bundle_component_qty">${item.qty}</span>` : ''}
        </div>
        <div class="bundle_component_title">
          ${item.title || ''}
        </div>
      </a>
    `;
  }).join('');
});

// Bundle variants should follow the source component product's variant order.
// Shopify may otherwise render their option values in numeric/title order.
(function initializeBundleVariantOrdering() {
  function reorderBundlePicker(picker) {
    const dataElement = picker?.querySelector('[data-bundle-variant-order]');
    // The bundle section can be outside the form when the theme partially
    // re-renders product blocks, so use the page-level marker as a fallback.
    const bundleSection = picker?.closest('product-form')?.querySelector('.product_bundles')
      || document.querySelector('.product_bundles');
    if (!dataElement && !bundleSection) return;

    let data = { bundle: true, variantCount: 2, options: {} };
    if (dataElement) {
      try {
        data = JSON.parse(dataElement.textContent || '{}');
      } catch (error) {
        console.error('Could not parse bundle variant order:', error);
        return;
      }
    }

    if (data.bundle !== true || Number(data.variantCount) <= 1) return;

    const configuredOptions = Object.entries(data.options || {});
    const options = configuredOptions.length
      ? configuredOptions
      : Array.from(picker.querySelectorAll('.product-form__option[data-option-position]'))
          .map((option) => [option.dataset.optionPosition, []]);

    options.forEach(([position, order]) => {
      const option = picker.querySelector(`.product-form__option[data-option-position="${position}"]`);
      if (!option || !Array.isArray(order)) return;

      const ranks = new Map(order.map((value, index) => [String(value), index]));
      const select = option.querySelector(`select[data-option-position="${position}"]`);

      // Some bundle products have already lost the source order by the time
      // Shopify exposes their option values. For volume options, recover the
      // same smallest-to-largest order used by the source product.
      const volumeInMillilitres = (label) => {
        const normalizedLabel = String(label || '')
          .replace(/[×x]/g, ' ')
          .replace(/,/g, '.')
          .trim();
        const match = normalizedLabel.match(/(?:^|[^\d])([0-9]+(?:\.[0-9]+)?)\s*(ml|millilit(?:re|er)s?|l|lit(?:re|er)s?)(?=$|[^a-z])/i);
        if (!match) return null;
        const amount = Number(match[1]);
        const unit = match[2].toLowerCase();
        return unit === 'l' || unit.startsWith('lit') ? amount * 1000 : amount;
      };

      const sortByVolumeWhenPossible = (items, getLabel) => {
        const volumeByItem = new Map(items.map((item) => [item, volumeInMillilitres(getLabel(item))]));
        if (Array.from(volumeByItem.values()).some((value) => value === null)) return false;
        items.sort((a, b) => volumeByItem.get(a) - volumeByItem.get(b));
        return true;
      };

      if (select) {
        const items = Array.from(select.options);
        if (!sortByVolumeWhenPossible(items, (item) => item.textContent)) {
          items.sort((a, b) => (ranks.get(String(a.value)) ?? order.length) - (ranks.get(String(b.value)) ?? order.length));
        }
        items.forEach((item) => select.appendChild(item));
        return;
      }

      const list = option.querySelector('.color-swatch-list, .variant-swatch-list, .block-swatch-list');
      if (!list) return;

      const items = Array.from(list.children);
      if (!sortByVolumeWhenPossible(items, (item) => {
        const labelledElement = item.querySelector('[title]');
        return labelledElement?.getAttribute('title') || item.textContent;
      })) {
        items.sort((a, b) => {
          const aValue = a.querySelector(`[data-option-position="${position}"]`)?.value;
          const bValue = b.querySelector(`[data-option-position="${position}"]`)?.value;
          return (ranks.get(String(aValue)) ?? order.length) - (ranks.get(String(bValue)) ?? order.length);
        });
      }
      items.forEach((item) => list.appendChild(item));
    });
  }

  function reorderAllBundlePickers() {
    document.querySelectorAll('variant-picker').forEach(reorderBundlePicker);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reorderAllBundlePickers);
  } else {
    reorderAllBundlePickers();
  }

  document.addEventListener('product:rerender', () => {
    window.setTimeout(reorderAllBundlePickers, 0);
  });

  document.addEventListener('variant:changed', () => {
    window.setTimeout(reorderAllBundlePickers, 0);
  });
})();

function handleSwatchChange(event){
  try {
    const container = event.target
    if(!container) return;

    const selectedOption1 = container.querySelector(`input[name="option1"]:checked`)
    console.log(selectedOption1)
    const option1Id = selectedOption1.getAttribute('value')
    const option1Value = window.options.option1.values_with_ids.find(a => a.id === parseInt(option1Id))
    console.log(option1Value)

    const option2ObjectsToShow = window.uniqueOptions.filter(a => a.first.id === option1Value.id)
    console.log(option2ObjectsToShow)

    const secondIdsToShow = option2ObjectsToShow.map(a => a.second.id)
    const allSecondInputs = document.querySelectorAll(`input[data-option-position="2"]`)
    console.log(secondIdsToShow)

    allSecondInputs.forEach(input => {
            const container = input.closest('.variant-swatch') || input.closest('.color-swatch') || input.closest('.block-swatch')

      hideSwatch(container)
    });

    secondIdsToShow.forEach(id => {
      const input = document.querySelector(`input[value="${id}"]`)
      const container = input.closest('.variant-swatch') || input.closest('.color-swatch') || input.closest('.block-swatch')
      console.log(input)
      showSwatch(container)
    });
  } catch (error) {
    console.error(error)
  }
}

function hideSwatch(wrapper) {
  // Step 1: animate
  wrapper.classList.add('is-hiding');

  // Step 2: after animation, add hidden class
  wrapper.addEventListener(
    'transitionend',
    function onEnd(e) {
      if (e.propertyName === 'opacity') {
        wrapper.classList.remove('is-hiding');
        wrapper.classList.add('is-hidden'); // <-- fully removes from layout
        wrapper.removeEventListener('transitionend', onEnd);
      }
    }
  );
}

function showSwatch(wrapper) {
  // Remove hidden first so it's back in layout
  wrapper.classList.remove('is-hidden');

  // Animate back in
  requestAnimationFrame(() => {
    wrapper.classList.remove('is-hiding');
  });
}


//quick links (brands) searching 
function searchBrands(value) {
  const searchValue = value.toLowerCase()

  const links = Array.from(document.querySelectorAll('.quick-links__link'))

  for (const link of links) {
    if (searchValue === '') {
      link.style.display = 'flex'
    } else {
      const linkValue = link.id.toLowerCase()
      if (linkValue.includes(searchValue)) {
        link.style.display = 'flex'
      } else {
        link.style.display = 'none'
      }
    }
  }

}















(function() {

  function parseLocations(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const script = doc.querySelector('.store-availability-container script');

    if (!script) return [];

    try {
      return JSON.parse(script.textContent.trim());
    } catch {
      return [];
    }
  }

  function renderPickup(container, locations, shipping) {
    if (!locations) {
      container.innerHTML = window.locale.store_availability.general.unavailable;
      return;
    }

      const available = shipping.innerHTML.includes('in-stock')

      const shippingIcon = available 
      ? window.icons['store-availability-in-stock']
      : window.icons['store-availability-out-of-stock'];

    container.innerHTML = `<div class="shipping-cart-availability" aria-hidden="true" name="shipping" method="shipping" active="false" available="${available}">` + `
        <span class="icon">${shippingIcon}</span>
        <div class="store-availability-information-container">
          <p class="store-availability-information__title text--strong">
            ${available ? window.locale.store_availability.shipping.available : window.locale.store_availability.shipping.unavailable}
          </p>
        </div>
    ` + '</div>'

    for(const location of locations){
          const icon = location.available
      ? window.icons['store-availability-in-stock']
      : window.icons['store-availability-out-of-stock'];

      container.innerHTML += `<div class="shipping-cart-availability" aria-hidden="true" name="pickup${location.name}" method="pickup" lat="${location.lat}" lng="${location.lng}" location="${location.name}" active="false" available="${location.available}">` + `
        <span class="icon">${icon}</span>
        <div class="store-availability-information-container">
          <p class="store-availability-information__title text--strong">
          ${
            location.available ? 
              window.locale.store_availability.general.pick_up_available_at.replace('{{ location_name }}', location.name)
            : window.locale.store_availability.general.pick_up_unavailable_at.replace('{{ location_name }}', location.name)
          }
          </p>
        </div>
      ` + '</div>'
    }
  }

  function render() {
    const blocks = document.querySelectorAll('.cart-store-availability');

    const promises = [...blocks].map(block => {

      const handle = block.dataset.productHandle;
      const variantId = block.dataset.variantId;

      if (!handle || !variantId) return Promise.resolve();

      return fetch(`/products/${handle}?variant=${variantId}`)
        .then(res => res.text())
        .then(html => {

          const doc = new DOMParser().parseFromString(html, 'text/html');

          const shopifyContainer = doc.querySelector('.store-availability-container');
          if (!shopifyContainer) {
            block.innerHTML = '';
            return;
          }

          const locations = parseLocations(html);
          if (!locations.length) {
            block.innerHTML = '';
            return;
          }

          const shipping = shopifyContainer.querySelector('.store-availability-shipping');

          renderPickup(block, locations, shipping);
        })
        .catch(err => {
          console.error(err);
          block.innerHTML = '';
        });
    });

    // 🔥 IMPORTANT: wait for ALL blocks to finish
    return Promise.all(promises);
  }


  const STORAGE_KEY = 'closest_store_v1';
  const CACHE_TTL = 1000 * 60 * 60 * 24 * 7;

  const selectEl = document.querySelector('.cart-pickup-store-select');
  const pickupRadio = document.querySelector('input[value="pickup"]');
  const shippingRadio = document.querySelector('input[value="shipping"]');

  if (!selectEl) return;

  // -------------------------
  // CACHE
  // -------------------------
  function getCache() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      if (!parsed.timestamp || Date.now() - parsed.timestamp > CACHE_TTL) return null;

      return parsed;
    } catch {
      return null;
    }
  }

  function setCache(data) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        ...data,
        timestamp: Date.now()
      }));
    } catch {}
  }

  // -------------------------
  // DISTANCE
  // -------------------------
  function distance(lat1, lon1, lat2, lon2) {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;

    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1 * Math.PI / 180) *
      Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) ** 2;

    return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
  }

  // -------------------------
  // BUILD STORE LIST FROM CART
  // -------------------------
  async function getCartStores() {

    const blocks = document.querySelectorAll('.shipping-cart-availability[method="pickup"]');
    const all = [];

    for (const block of blocks) {

      const locationName = block.getAttribute('location')
      const lat = block.getAttribute('lat')
      const lng = block.getAttribute('lng')
      
      const obj = { lat, lng, locationId: locationName, name: locationName }

      const exists = all.find(a => a.locationId === obj.locationId)

      if(!exists) all.push(obj)
    }

    return all;
  }

  // -------------------------
  // FIND CLOSEST
  // -------------------------
  function findClosest(locations, lat, lng) {
    return locations
      .filter(l => l.lat && l.lng)
      .map(l => ({
        ...l,
        dist: distance(lat, lng, l.lat, l.lng)
      }))
      .sort((a, b) => a.dist - b.dist)[0];
  }

  // -------------------------
  // RENDER DROPDOWN
  // -------------------------
  function renderDropdown(locations, selected) {

    selectEl.innerHTML = '';

    locations.forEach(loc => {
      const opt = document.createElement('option');
      opt.value = loc.name;
      opt.textContent = `${loc.name}`;
      if (selected && loc.name === selected.name) opt.selected = true;
      selectEl.appendChild(opt);
    });

    selectEl.addEventListener('change', () => {
      const chosen = locations.find(l => l.name === selectEl.value);

      setCache({
        lat: chosen?.lat,
        lng: chosen?.lng,
        locationId: chosen?.name
      });
    });
  }

  // -------------------------
  // MAIN
  // -------------------------
  async function init() {

    const locations = await getCartStores();

    if (!locations.length) return;

    const cache = getCache();

    const apply = (lat, lng) => {
      const closest = findClosest(locations, lat, lng) || locations[0];

      setCache({
        lat,
        lng,
        locationId: closest.name
      });

      renderDropdown(locations, closest);
    };

    // -------------------------
    // CACHE FIRST
    // -------------------------
    if (cache?.lat && cache?.lng) {
      apply(cache.lat, cache.lng);
      return;
    }

    // -------------------------
    // GEOLOCATION (NOW AWAITED)
    // -------------------------
    const position = await new Promise(resolve => {

      if (!navigator.geolocation) {
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        pos => resolve(pos),
        () => resolve(null)
      );
    });

    if (position) {
      apply(position.coords.latitude, position.coords.longitude);
    } else {
      apply(locations[0].lat, locations[0].lng);
    }
  }

  (async function (){
    await render()
    await init()

    const radios = document.querySelectorAll(`input[name="attributes[delivery_method]"]`)
    for(const radio of radios){
      console.log(radio,radio.disabled)
      radio.disabled = false
    }

    const selectedLocation = document.querySelector(`.cart-pickup-store-select`)
    console.log(selectedLocation,selectedLocation.disabled)
    selectedLocation.disabled = false


    const checkoutButton = document.querySelector(`button[name="checkout"]`)
    checkoutButton.setAttribute('type','button')
    checkoutButton.setAttribute('onclick', 'verifyCart()')

    const preferred_delivery_method = await getPreferedDeliveryMethod()
    if(preferred_delivery_method){
      const radio = document.querySelector(`input[name="attributes[delivery_method]"][value="${preferred_delivery_method}"]`)
      console.log(preferred_delivery_method,radio)
      if(radio) radio.checked = true
    }
  })();
})();

window.__cartUnavailableItems = [];

function verifyCart(el){
  try {
    const pickupRadio = document.querySelector(`input[name="attributes[delivery_method]"][value="pickup"]`)
    const shippingRadio = document.querySelector(`input[name="attributes[delivery_method]"][value="shipping"]`)

    if(el && el.tagName === 'SELECT') {
      pickupRadio.checked = true
      shippingRadio.checked = false
    }

    const button = document.querySelector(`button[name="checkout"]`)

    const selectedRadio = document.querySelector(`input[name="attributes[delivery_method]"]:checked`)
    const selectedLocation = document.querySelector(`.cart-pickup-store-select`)

    if(!selectedRadio){
      const error_text = document.getElementById('cart-pickup-error-text')

      const pickupLabel = pickupRadio.closest('label')
      const shippingLabel = shippingRadio.closest('label')
      pickupLabel.classList.add('error')
      shippingLabel.classList.add('error')
      error_text.setAttribute('aria-hidden','false')
      setTimeout(() => {
        error_text.setAttribute('aria-hidden','true')
        pickupLabel.classList.remove('error')
        shippingLabel.classList.remove('error')
      }, 5000);
      return
    }

    const method = selectedRadio.value
    const location = selectedLocation.value

    setPreferedDeliveryMethod(method)

    const icons = document.querySelectorAll('.cart-method-option__icon')
    for(const icon of icons){
      icon.remove()
    }

    let isAllAvailable = true
    const unavailable = []

    const elementKey = `${method}${method !== 'shipping' ? location : ''}`
    let elements = document.querySelectorAll(`.shipping-cart-availability[name="${elementKey}"]`);

    const blocks = document.querySelectorAll('.shipping-cart-availability');
    for(const block of blocks){
      block.setAttribute('aria-hidden','true')
    }

    elements.forEach(element => {
      const available = element.getAttribute('available')
      const isAvailable = available === 'true'

      const container = element.parentNode

      const variantId = container?.getAttribute('data-variant-id') ?? null
      const productHandle = container?.getAttribute('data-product-handle') ?? null
      const obj = {variantId,productHandle}

      element.setAttribute('aria-hidden','false')
      if(!isAvailable) isAllAvailable = false
      if(variantId && productHandle && !isAvailable) unavailable.push(obj)
    });

    const parent = selectedRadio.closest('.cart-method-option')
    const optionUi = parent.querySelector('.cart-method-option__ui')

    let iconHTML = ''

    if(!isAllAvailable){ 
      iconHTML = `${window.icons['store-availability-out-of-stock']} ${window.locale.store_availability.general.unavailable}`
      window.__cartUnavailableItems = unavailable;
    
      button.setAttribute('type','button')
      button.setAttribute('onclick', 'verifyCart()')
    
      buildUnavailableModal(unavailable);
    
      // optional auto-open
      openUnavailableModal();
    
    } else {
      iconHTML = `${window.icons['store-availability-in-stock']} ${window.locale.store_availability.general.available}`
      
      button.setAttribute('type','submit')
      button.removeAttribute('onclick')

      window.__cartUnavailableItems = [];
    }
    
    const icon = optionUi.querySelector('.cart-method-option__icon') || document.createElement('div')
    icon.classList.add('cart-method-option__icon')
    icon.innerHTML = iconHTML
    optionUi.appendChild(icon)
  } catch (error) {
    console.error(error)
    button.disabled = false
    button.setAttribute('type','submit')
    button.removeAttribute('onclick')
  } finally {
  }
}

function buildUnavailableModal(items) {

  const root = document.getElementById('cart-unavailable-modal-root');

  const allVariantIds = []

  root.innerHTML = `
    <div id="cart-unavailable-modal-overlay">
      <div id="cart-unavailable-modal">

        <h3>${window.locale.store_availability.form.items_unavailable}</h3>

        <div id="cart-unavailable-list">
          ${items.map(i => {
            const product = window.__PRODUCT_MAP__?.[i.variantId];

            allVariantIds.push(parseInt(product.variant_id))

            const fullTitle = product?.variant_title
              ? `${product.title} - ${product.variant_title}`
              : product?.title || i.productHandle;

            return `
              <div class="cart-unavailable-item">
                <strong>${fullTitle}</strong><br>
                <div class="cart-unavailable-item-remove" onclick="removeCartItemsByVariantIds('${product.variant_id}', false, this)">${window.locale.cart.items.remove}</div>
              </div>
            `;
          }).join('')}
        </div>

        <button id="cart-unavailable-close">${window.locale.store_availability.form.close}</button>
        <button id="cart-unavailable-remove" onclick="removeCartItemsByVariantIds('${allVariantIds.join(',')}', true, this)">${window.locale.store_availability.form.remove_all}</button>
      </div>
    </div>
  `;

  // EVENTS
  const overlay = document.getElementById('cart-unavailable-modal-overlay');
  const closeBtn = document.getElementById('cart-unavailable-close');

  closeBtn.addEventListener('click', closeUnavailableModal);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closeUnavailableModal();
  });
}

let changesCount = 0
async function removeCartItemsByVariantIds(variantIds = [], closeModal = false, element) {
  if(element) element.innerHTML = window.icons['search-loader']

  if(typeof(variantIds) === 'string') variantIds = variantIds.split(',')
  if (!Array.isArray(variantIds) || !variantIds.length) return;

  try {
    const results = await Promise.all(
      variantIds.map(variantId =>
        fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: variantId,
            quantity: 0
          })
        }).then(res => res.json())
      )
    );

    if(element){
      const parent = element.parentNode
      parent.classList.add('removed')
    }

    console.log('Removed items:', results);
    changesCount += results.length

    if(closeModal) closeUnavailableModal()

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh')
    );

    return results;
  } catch (err) {
    console.error('Failed to remove cart items:', err);
  } finally {
    if(element) element.innerHTML = window.icons['store-availability-in-stock']
  }
}

function openUnavailableModal() {
  const overlay = document.getElementById('cart-unavailable-modal-overlay');
  if (overlay) overlay.style.display = 'flex';
}

function closeUnavailableModal() {
  const overlay = document.getElementById('cart-unavailable-modal-overlay');
  if (overlay) overlay.style.display = 'none';
  if(changesCount > 0) location.reload()
}


const delivery_method_key = 'preferred_delivery_method'
const TTL_MS = 7 * 24 * 60 * 60 * 1000 // 7 days

function setPreferedDeliveryMethod(method) {
  const payload = {
    value: method,
    expiry: Date.now() + TTL_MS
  }

  localStorage.setItem(delivery_method_key, JSON.stringify(payload))
}

function getPreferedDeliveryMethod() {
  try {
    const raw = localStorage.getItem(delivery_method_key)
    if (!raw) return null

    const parsed = JSON.parse(raw)

    // invalid shape safeguard
    if (!parsed || typeof parsed !== 'object') {
      localStorage.removeItem(delivery_method_key)
      return null
    }

    // expired
    if (Date.now() > parsed.expiry) {
      localStorage.removeItem(delivery_method_key)
      return null
    }

    return parsed.value
  } catch (e) {
    // corrupted JSON, clean it up
    localStorage.removeItem(delivery_method_key)
    return null
  }
}
/**
 * Automatic discount metafield hybrid pricing
 *
 * Discounts with NO minimum requirement are safe to show as a real price change.
 * Discounts WITH quantity/subtotal minimums keep the theme price untouched and
 * show an unlock message underneath the price instead. ACTIVE and SCHEDULED
 * snapshots are gated by startsAt/endsAt because Shopify does not resend a
 * webhook when a scheduled automatic discount begins.
 */
(function () {
  const MAP_SELECTOR = 'script[type="application/json"][data-automatic-discount-map]';
  const PRICE_SELECTOR = '[data-automatic-discount-price-list]';
  const LINE_TOTAL_SELECTOR = '[data-automatic-discount-line-total]';
  const TARGET_SELECTOR = `${PRICE_SELECTOR}, ${LINE_TOTAL_SELECTOR}`;
  const MESSAGE_SELECTOR = '.automatic-discount-message';

  const state = window.__automaticDiscountPromos = window.__automaticDiscountPromos || {
    variants: {},
    products: {},
    moneyFormat: '${{amount}}',
    moneyWithCurrencyFormat: '${{amount}} CAD',
    currencyCodeEnabled: false,
    customerDiscount: 0,
    customerId: '',
    customerSegments: [],
    customerOffers: [],
    parsedScripts: new WeakSet()
  };

  const originalHtmlByElement = new WeakMap();

  function parseJsonSafely(value, fallback) {
    if (value == null || value === '') return fallback;
    if (typeof value !== 'string') return value;

    try {
      return JSON.parse(value);
    } catch (error) {
      return fallback;
    }
  }

  function normalizeDiscounts(discounts) {
    discounts = parseJsonSafely(discounts, []);

    if (typeof discounts === 'string') {
      discounts = parseJsonSafely(discounts, []);
    }

    if (!Array.isArray(discounts)) {
      discounts = discounts ? [discounts] : [];
    }

    return discounts
      .map(discount => (typeof discount === 'string' ? parseJsonSafely(discount, null) : discount))
      .filter(Boolean);
  }

  function discontinuedPercentage(value) {
    const text = String(value ?? '').trim();
    if (!/^\d+(?:\.\d+)?%?$/.test(text)) return 0;
    const percentage = Number(text.replace('%', ''));
    return Number.isFinite(percentage) && percentage > 0 && percentage <= 90 ? percentage : 0;
  }

  function toIntegerCents(value) {
    if (value == null || value === '') return 0;
    const number = Number(value);
    if (!Number.isFinite(number)) return 0;
    return number;
  }

  function registerMap(map) {
    if (!map || typeof map !== 'object') return;

    if (map.moneyFormat) state.moneyFormat = map.moneyFormat;
    if (map.moneyWithCurrencyFormat) state.moneyWithCurrencyFormat = map.moneyWithCurrencyFormat;
    if (typeof map.currencyCodeEnabled === 'boolean') state.currencyCodeEnabled = map.currencyCodeEnabled;
    if (Object.prototype.hasOwnProperty.call(map, 'customerDiscount')) {
      state.customerDiscount = discontinuedPercentage(map.customerDiscount);
      state.customerId = String(map.customerId || '');
      state.customerSegments = Array.isArray(map.customerSegments) ? map.customerSegments : [];
      state.customerOffers = Array.isArray(map.customerOffers) ? map.customerOffers.map(offer => ({ ...offer, value: { percentage: Number(offer.percent || 0) / 100 } })) : [];
    }

    const productId = map.productId != null ? String(map.productId) : null;

    if (productId) {
      state.products[productId] = {
        ...(state.products[productId] || {}),
        id: productId,
        handle: map.handle || state.products[productId]?.handle || '',
        title: map.title || state.products[productId]?.title || '',
        url: map.url || state.products[productId]?.url || '',
        image: map.image || state.products[productId]?.image || '',
        variants: state.products[productId]?.variants || {}
      };
    }

    Object.keys(map.variants || {}).forEach(variantId => {
      const variant = map.variants[variantId] || {};
      const normalizedVariant = {
        id: String(variant.id || variantId),
        productId: String(variant.productId || productId || ''),
        productTitle: variant.productTitle || map.title || '',
        productHandle: variant.productHandle || map.handle || '',
        productUrl: variant.productUrl || map.url || '',
        productImage: variant.productImage || map.image || '',
        price: toIntegerCents(variant.price),
        compareAtPrice: toIntegerCents(variant.compareAtPrice || variant.compare_at_price),
        available: variant.available !== false,
        inventoryQuantity: Number(variant.inventoryQuantity ?? variant.inventory_quantity ?? 0),
        title: variant.title || '',
        sku: variant.sku || '',
        discounts: normalizeDiscounts(variant.discounts),
        discontinuedDiscount: discontinuedPercentage(variant.discontinuedDiscount ?? map.discontinuedDiscount ?? state.variants[String(variant.id || variantId)]?.discontinuedDiscount),
        bundleProduct: variant.bundleProduct ?? map.bundleProduct ?? state.variants[String(variant.id || variantId)]?.bundleProduct ?? false,
        bundleCollectionMember: variant.bundleCollectionMember ?? map.bundleCollectionMember ?? state.variants[String(variant.id || variantId)]?.bundleCollectionMember ?? false
      };

      state.variants[normalizedVariant.id] = normalizedVariant;

      if (normalizedVariant.productId) {
        state.products[normalizedVariant.productId] = {
          ...(state.products[normalizedVariant.productId] || {}),
          id: normalizedVariant.productId,
          handle: state.products[normalizedVariant.productId]?.handle || normalizedVariant.productHandle || '',
          title: state.products[normalizedVariant.productId]?.title || normalizedVariant.productTitle || '',
          url: state.products[normalizedVariant.productId]?.url || normalizedVariant.productUrl || '',
          image: state.products[normalizedVariant.productId]?.image || normalizedVariant.productImage || '',
          variants: state.products[normalizedVariant.productId]?.variants || {}
        };

        state.products[normalizedVariant.productId].variants[normalizedVariant.id] = normalizedVariant;
      }
    });
  }

  function collectMaps(root) {
    (root || document).querySelectorAll(MAP_SELECTOR).forEach(script => {
      if (state.parsedScripts.has(script)) return;
      state.parsedScripts.add(script);
      registerMap(parseJsonSafely((script.textContent || '').trim(), null));
    });
  }

  function parseDate(value) {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  function isDiscountActive(discount, now) {
    if (!discount || typeof discount !== 'object') return false;
    // Quantity-based offers depend on the cart. Never present them as a single-item sale price.
    if (discount.type === 'minimum_quantity' || discount.type === 'bxgy') return false;

    const status = String(discount.status || '').trim().toUpperCase();
    const startsAt = parseDate(discount.startsAt || discount.starts_at);
    const endsAt = parseDate(discount.endsAt || discount.ends_at);

    // Shopify does not resend a webhook when a scheduled automatic discount reaches
    // its start time, so variant metafields can still say SCHEDULED even while the
    // discount is live. Trust the schedule window for ACTIVE/SCHEDULED snapshots.
    if (status && status !== 'ACTIVE' && status !== 'SCHEDULED') return false;
    if (status === 'SCHEDULED' && !startsAt) return false;

    if (startsAt && startsAt.getTime() > now) return false;
    if (endsAt && endsAt.getTime() <= now) return false;

    return true;
  }

  function readDiscountValue(discount) {
    const value = discount.value || discount.discountValue || discount.discount_value || {};
    const type = String(value.type || discount.valueType || '').toLowerCase();

    if (value.percentage != null || discount.percentage != null || type.includes('percentage')) {
      let percentage = Number(value.percentage != null ? value.percentage : discount.percentage);
      if (!Number.isFinite(percentage)) percentage = 0;
      if (percentage > 1) percentage = percentage / 100;

      return {
        type: 'percentage',
        amount: Math.max(0, percentage)
      };
    }

    const amountValue = value.amount || value.fixedAmount || value.fixed_amount || discount.amount || discount.fixedAmount || discount.fixed_amount;
    let amount = 0;

    if (amountValue && typeof amountValue === 'object') {
      amount = Number(amountValue.amount || amountValue.value || amountValue.cents || 0);
    } else {
      amount = Number(amountValue || 0);
    }

    if (!Number.isFinite(amount)) amount = 0;

    return {
      type: 'fixed_amount',
      amount: amount > 1000 ? Math.round(amount) : Math.round(amount * 100)
    };
  }

  function calculateDiscountedPrice(priceCents, discount, compareAtPriceCents = 0) {
    const price = Math.max(0, toIntegerCents(priceCents));
    const value = readDiscountValue(discount);

    if (value.type === 'percentage') {
      if (discount.regularPriceOnly === true && compareAtPriceCents > price) {
        const regular = Math.max(0, toIntegerCents(compareAtPriceCents));
        return Math.min(price, Math.max(0, Math.round(regular * (1 - value.amount))));
      }
      // Shopify rounds the discount amount down to cents, then subtracts it.
      return Math.max(0, price - Math.floor(price * value.amount));
    }

    if (value.type === 'fixed_amount') {
      return Math.max(0, price - Math.round(value.amount || 0));
    }

    return price;
  }

  function discountSavingsScore(variant, discount) {
    if (!variant || !discount) return 0;
    return Math.max(0, (variant.price || 0) - calculateDiscountedPrice(variant.price || 0, discount, variant.compareAtPrice || 0));
  }

  function toPositiveNumber(value) {
    if (value == null || value === '') return null;
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : null;
  }

  function toMoneyCents(value) {
    if (value == null || value === '') return null;

    if (typeof value === 'object') {
      value = value.amount || value.value || value.cents || null;
    }

    const text = String(value == null ? '' : value).trim();
    if (!text) return null;

    const number = Number(text.replace(/[^0-9.-]/g, ''));
    if (!Number.isFinite(number) || number <= 0) return null;

    if (text.includes('.')) return Math.round(number * 100);
    return number > 10000 ? Math.round(number) : Math.round(number * 100);
  }

  function findRequirementValue(source, keys, depth) {
    if (!source || typeof source !== 'object' || depth > 5) return null;

    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        const value = source[key];
        if (value && typeof value === 'object') {
          const amount = toPositiveNumber(value.amount || value.value || value.quantity || value.cents);
          if (amount) return value;
        } else if (value != null && value !== '') {
          return value;
        }
      }
    }

    for (const value of Object.values(source)) {
      if (value && typeof value === 'object') {
        const nestedValue = findRequirementValue(value, keys, depth + 1);
        if (nestedValue != null) return nestedValue;
      }
    }

    return null;
  }

  function getMinimumRequirement(discount) {
    const requirement = discount.minimumRequirement ||
      discount.minimum_requirement ||
      discount.minimumRequirements ||
      discount.minimum_requirements ||
      discount.prerequisite ||
      discount.requirement ||
      discount.requirements ||
      null;

    if (!requirement) return null;

    if (typeof requirement === 'string') {
      const textRequirement = requirement.trim().toLowerCase();
      if (!textRequirement || textRequirement === 'none' || textRequirement === 'null') return null;
      return null;
    }

    if (typeof requirement !== 'object') return null;

    const requirementType = String(requirement.type || requirement.__typename || requirement.name || '').toLowerCase();
    if (requirementType.includes('none') || requirementType.includes('no_')) return null;

    const quantityValue = findRequirementValue(requirement, [
      'greaterThanOrEqualToQuantity',
      'greater_than_or_equal_to_quantity',
      'minimumQuantity',
      'minimum_quantity',
      'quantity',
      'minQuantity',
      'min_quantity'
    ], 0);

    const quantity = toPositiveNumber(
      quantityValue && typeof quantityValue === 'object'
        ? quantityValue.amount || quantityValue.value || quantityValue.quantity
        : quantityValue
    );

    if (quantity && quantity > 1) {
      return {
        type: 'quantity',
        quantity: Math.ceil(quantity)
      };
    }

    const subtotalValue = findRequirementValue(requirement, [
      'greaterThanOrEqualToSubtotal',
      'greater_than_or_equal_to_subtotal',
      'minimumSubtotal',
      'minimum_subtotal',
      'subtotal',
      'amount',
      'minSubtotal',
      'min_subtotal'
    ], 0);

    const subtotalCents = toMoneyCents(subtotalValue);

    if (subtotalCents) {
      return {
        type: 'subtotal',
        amountCents: subtotalCents
      };
    }

    return null;
  }

  // The metafield is a generic automatic-discount percentage. The SKU and
  // product type determine its customer-facing name, never the metafield key.
  function getDiscontinuedDiscountCandidate(variant, allowUnavailablePerpetual = false) {
    const percentage = discontinuedPercentage(variant?.discontinuedDiscount);
    if (!percentage || (!allowUnavailablePerpetual && Number(variant.inventoryQuantity || 0) <= 0) || variant.compareAtPrice > variant.price) return null;
    const isClearance = /\|DISC$/i.test(String(variant.sku || '').trim());
    const title = isClearance ? 'Clearance Discount' : variant.bundleProduct ? 'Bundle Discount' : 'Discount';
    const discount = { id: `metafield-discount-${percentage}`, title, status: 'ACTIVE', value: { percentage } };
    return {
      variant,
      discount,
      savingsScore: discountSavingsScore(variant, discount),
      discountedPrice: calculateDiscountedPrice(variant.price || 0, discount, variant.compareAtPrice || 0),
      minimumRequirement: null,
      endsAt: null
    };
  }
  function isEligibleCustomerDiscount(discount) {
    const eligibility = discount?.eligibility;
    if (!eligibility || eligibility.type === 'all') return true;
    if (!state.customerDiscount || !state.customerId || discontinuedPercentage(discount.percent) !== state.customerDiscount) return false;
    const ids = Array.isArray(eligibility.selections) ? eligibility.selections.map(item => String(item.id)) : [];
    if (eligibility.type === 'customers') return ids.includes(state.customerId);
    if (eligibility.type === 'segments') return ids.some(id => state.customerSegments.includes(id));
    return false;
  }
  function getActiveDiscountCandidates(variant, now, allowUnavailablePerpetual = false) {
    if (!variant || (variant.available === false && !allowUnavailablePerpetual) || !Array.isArray(variant.discounts)) return [];

    const activeDiscounts = [...variant.discounts, ...state.customerOffers.filter(offer => offer?.allProducts === true && offer?.eligibility?.type !== 'all')]
      .filter(discount => isEligibleCustomerDiscount(discount) && isDiscountActive(discount, now) && (variant.available !== false || !parseDate(discount.endsAt || discount.ends_at)) && !(variant.compareAtPrice > variant.price && /^clear(?:e|a)nce discount$/i.test(String(discount.title || '').trim())) && (variant.bundleCollectionMember === true || !String(discount.id || '').endsWith('/1532683190351')))
      .map(discount => {
        const savingsScore = discountSavingsScore(variant, discount);

        return {
          variant,
          discount,
          savingsScore,
          discountedPrice: calculateDiscountedPrice(variant.price || 0, discount, variant.compareAtPrice || 0),
          minimumRequirement: getMinimumRequirement(discount),
          endsAt: parseDate(discount.endsAt || discount.ends_at)
        };
      })
      .filter(candidate => candidate.savingsScore > 0);
    // Keep the metafield discount alongside other active promotions so the best offer wins.
    const discontinued = getDiscontinuedDiscountCandidate(variant, allowUnavailablePerpetual);
    return discontinued?.savingsScore > 0 ? [...activeDiscounts, discontinued] : activeDiscounts;
  }

  function getUpcomingDiscountCandidates(variant, now) {
    if (!variant || variant.available === false || !Array.isArray(variant.discounts)) return [];
    const upcomingWindowMs = 36 * 60 * 60 * 1000;
    return variant.discounts.filter(isEligibleCustomerDiscount).map(discount => {
      if (!discount || typeof discount !== 'object') return null;
      if (variant.compareAtPrice > variant.price && /^clear(?:e|a)nce discount$/i.test(String(discount.title || '').trim())) return null;
      if (variant.bundleCollectionMember !== true && String(discount.id || '').endsWith('/1532683190351')) return null;
      const status = String(discount.status || '').trim().toUpperCase();
      if (status && status !== 'SCHEDULED' && status !== 'ACTIVE') return null;
      const startsAt = parseDate(discount.startsAt || discount.starts_at);
      const endsAt = parseDate(discount.endsAt || discount.ends_at);
      if (!startsAt || startsAt.getTime() <= now || startsAt.getTime() - now > upcomingWindowMs) return null;
      if (endsAt && endsAt.getTime() <= startsAt.getTime()) return null;
      const savingsScore = discountSavingsScore(variant, discount);
      if (savingsScore <= 0) return null;
      return { variant, discount, startsAt, savingsScore, discountedPrice: calculateDiscountedPrice(variant.price || 0, discount, variant.compareAtPrice || 0), minimumRequirement: getMinimumRequirement(discount) };
    }).filter(Boolean).sort((a, b) => a.startsAt.getTime() - b.startsAt.getTime() || b.savingsScore - a.savingsScore);
  }
  function getNextDiscountWindowTime(discount, now) {
    if (!discount || typeof discount !== 'object') return null;

    const status = String(discount.status || '').trim().toUpperCase();
    if (status && status !== 'ACTIVE' && status !== 'SCHEDULED') return null;

    const startsAt = parseDate(discount.startsAt || discount.starts_at);
    const endsAt = parseDate(discount.endsAt || discount.ends_at);

    if (startsAt && startsAt.getTime() > now) {
      const upcomingBoundary = startsAt.getTime() - (36 * 60 * 60 * 1000);
      return upcomingBoundary > now ? upcomingBoundary : startsAt.getTime();
    }
    if (endsAt && endsAt.getTime() > now) return endsAt.getTime();

    return null;
  }

  let discountWindowTimer = null;

  function scheduleNextDiscountWindowRender() {
    window.clearTimeout(discountWindowTimer);

    const now = Date.now();
    let nextTime = null;

    Object.values(state.variants || {}).forEach(variant => {
      (variant.discounts || []).forEach(discount => {
        const discountWindowTime = getNextDiscountWindowTime(discount, now);
        if (!discountWindowTime) return;
        if (nextTime == null || discountWindowTime < nextTime) nextTime = discountWindowTime;
      });
    });

    if (nextTime == null) return;

    const maxDelay = 2147483647;
    const delay = Math.min(Math.max(0, nextTime - now + 250), maxDelay);

    discountWindowTimer = window.setTimeout(() => {
      renderAll(document);
    }, delay);
  }

  function pickBestBySavings(candidates) {
    return candidates.reduce((best, candidate) => {
      if (!best) return candidate;
      if (candidate.savingsScore > best.savingsScore) return candidate;
      if (candidate.savingsScore === best.savingsScore && candidate.discountedPrice < best.discountedPrice) return candidate;
      return best;
    }, null);
  }

  function pickLowestPrice(candidates) {
    return candidates.reduce((best, candidate) => {
      if (!best) return candidate;
      if (candidate.discountedPrice < best.discountedPrice) return candidate;
      if (candidate.discountedPrice === best.discountedPrice && candidate.savingsScore > best.savingsScore) return candidate;
      return best;
    }, null);
  }

  function getVariant(variantId) {
    if (!variantId) return null;
    return state.variants[String(variantId)] || null;
  }

  function getProductVariants(productId) {
    const product = productId ? state.products[String(productId)] : null;
    if (!product) return [];
    return Object.values(product.variants || {});
  }

  function promotionKey(candidate) {
    const discount = candidate?.discount || {};
    const requirement = candidate?.minimumRequirement || {};
    const discountId = discount.id || discount.numericId || discount.title || 'automatic-promotion';
    const requirementValue = requirement.type === 'quantity'
      ? requirement.quantity
      : requirement.type === 'subtotal'
        ? requirement.amountCents
        : '';
    return `${discountId}:${requirement.type || ''}:${requirementValue}`;
  }

  function getCartPromotionQuantity(candidate, element) {
    const mode = element.getAttribute('data-automatic-discount-mode');
    if (mode !== 'cart-line' && mode !== 'cart-line-total') {
      return Math.max(1, Number(element.getAttribute('data-quantity') || 1));
    }

    const targetKey = promotionKey(candidate);
    const lineElements = document.querySelectorAll(
      `${PRICE_SELECTOR}[data-automatic-discount-mode="cart-line"]`
    );
    let quantity = 0;

    lineElements.forEach(lineElement => {
      const variant = getVariant(lineElement.getAttribute('data-variant-id'));
      if (!variant) return;

      const hasPromotion = getActiveDiscountCandidates(variant, Date.now())
        .some(lineCandidate => promotionKey(lineCandidate) === targetKey);
      if (!hasPromotion) return;

      quantity += Math.max(0, Number(lineElement.getAttribute('data-quantity') || 0));
    });

    // Keep the message useful during partial cart DOM updates, before all line
    // elements have been rendered.
    return quantity || Math.max(1, Number(element.getAttribute('data-quantity') || 1));
  }

  async function getPromotionCatalog(candidate) {
    const targetKey = promotionKey(candidate);
    const results = [];

    // Discount sync payloads include the authoritative product target list.
    // Use it first so the picker is not limited to products present in the cart
    // or to whatever products a storefront collection happens to expose.
    const targets = [...new Map((candidate.discount?.getTargets || [])
      .filter(target => target?.type === 'product' && target.handle)
      .map(target => [String(target.id || target.handle), target])).values()];
    if (targets.length) {
      const loaded = [];
      const loadTarget = async target => {
        try {
          const rootUrl = String(window.routes?.rootUrl || '/').replace(/\/$/, '');
          const response = await fetch(`${rootUrl}/products/${encodeURIComponent(target.handle)}.js`, { headers: { Accept: 'application/json' } });
          if (!response.ok) return [];
          const product = await response.json();
          const variants = (product.variants || [])
            .filter(variant => variant.available !== false)
            .filter(variant => !target.variantId || String(variant.id) === String(target.variantId))
            .map(variant => ({
              id: String(variant.id),
              title: variant.title || '',
              price: Math.round(Number(variant.price || 0) * 100),
              available: variant.available !== false,
              inventoryQuantity: Number(variant.inventory_quantity || 0),
              discounts: candidate.discount ? [candidate.discount] : []
            }));
          return variants.map(variant => ({
            product: {
              id: String(product.id || target.id || ''),
              handle: product.handle || target.handle,
              title: product.title || target.title || String(target.handle || 'Eligible product').split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
              url: product.url || `/products/${target.handle}`,
              image: product.featured_image || product.images?.[0] || ''
            },
            variant,
            candidate
          }));
        } catch (_) {
          return [];
        }
      };
      for (let index = 0; index < targets.length; index += 6) {
        loaded.push(...await Promise.all(targets.slice(index, index + 6).map(loadTarget)));
      }
      const targetedResults = loaded.flat();
      if (targetedResults.length) return targetedResults;
    }

    Object.values(state.products || {}).forEach(product => {
      Object.values(product.variants || {}).forEach(variant => {
        if (variant.available === false || Number(variant.inventoryQuantity || 0) <= 0) return;
        const match = getActiveDiscountCandidates(variant, Date.now())
          .find(item => promotionKey(item) === targetKey);
        if (!match) return;

        results.push({ product, variant, candidate: match });
      });
    });

    return results.sort((a, b) => {
      const productTitle = String(a.product.title || a.product.handle || '');
      const otherTitle = String(b.product.title || b.product.handle || '');
      return productTitle.localeCompare(otherTitle) || String(a.variant.title || '').localeCompare(String(b.variant.title || ''));
    });
  }

  function closePromotionPicker() {
    const root = document.getElementById('automatic-discount-promo-modal-root');
    if (root) root.innerHTML = '';
  }

  async function addPromotionVariant(variantId, button) {
    if (!variantId || !button) return;
    button.disabled = true;
    button.textContent = 'Adding…';

    try {
      const response = await fetch(window.routes?.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
      });
      if (!response.ok) throw new Error('Could not add this product to the cart.');

      closePromotionPicker();
      document.documentElement.dispatchEvent(new CustomEvent('product:added', { bubbles: true, detail: { quantity: 1 } }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      window.setTimeout(() => window.location.reload(), 150);
    } catch (error) {
      button.disabled = false;
      button.textContent = 'Add';
      window.alert(error.message || 'Could not add this product to the cart.');
    }
  }

  async function getLiveCartPromotionQuantity(candidate) {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return 0;
      const cart = await response.json();
      const targetKey = promotionKey(candidate);
      return (cart.items || []).reduce((total, item) => {
        const variant = getVariant(item.variant_id);
        if (!variant) return total;
        const matches = getActiveDiscountCandidates(variant, Date.now())
          .some(itemCandidate => promotionKey(itemCandidate) === targetKey);
        return matches ? total + Number(item.quantity || 0) : total;
      }, 0);
    } catch (_) {
      return 0;
    }
  }

  function promotionDescription(candidate, element, quantity) {
    const requirement = candidate?.minimumRequirement;
    const title = String(candidate?.discount?.title || 'Automatic promotion').trim();
    const discountText = getDiscountText(candidate.discount, element);
    if (requirement?.type === 'quantity') {
      return `${title}: buy ${requirement.quantity}+ eligible items and save ${discountText} on each eligible item automatically at checkout.`;
    }
    return `${title}: ${discountText} automatically at checkout when the promotion requirements are met.`;
  }

  async function openPromotionPicker(candidate, needed) {
    const root = document.getElementById('automatic-discount-promo-modal-root');
    if (!root || !candidate || needed <= 0) return;

    const products = await getPromotionCatalog(candidate);
    if (!products.length) return;

    root.innerHTML = `
      <div class="automatic-discount-promo-modal__overlay" data-promo-modal-close>
        <div class="automatic-discount-promo-modal" role="dialog" aria-modal="true" aria-labelledby="automatic-discount-promo-title">
          <button type="button" class="automatic-discount-promo-modal__close" data-promo-modal-close aria-label="Close">&times;</button>
          <h2 id="automatic-discount-promo-title">${htmlEscape(candidate.discount?.title || 'Complete your promo')}</h2>
          <p>${htmlEscape(promotionDescription(candidate, root, needed))} You need ${needed} more eligible item${needed === 1 ? '' : 's'}.</p>
          <div class="automatic-discount-promo-modal__products">
            ${products.map(({ product, variant }) => `
              <article class="automatic-discount-promo-modal__product">
                ${product.image ? `<img src="${htmlEscape(product.image)}" alt="">` : '<div class="automatic-discount-promo-modal__placeholder"></div>'}
                <div class="automatic-discount-promo-modal__product-info">
                  <strong>${htmlEscape(product.title || product.handle || 'Eligible product')}</strong>
                  ${variant.title && variant.title !== 'Default Title' ? `<span>${htmlEscape(variant.title)}</span>` : ''}
                  <span>${formatMoney(variant.price, getCurrencyPreference(root))}</span>
                </div>
                <button type="button" class="button button--primary automatic-discount-promo-modal__add" data-promo-variant-id="${htmlEscape(variant.id)}">Add</button>
              </article>
            `).join('')}
          </div>
        </div>
      </div>
    `;

    root.querySelectorAll('[data-promo-modal-close]').forEach(element => {
      element.addEventListener('click', event => {
        if (event.target === element || element.classList.contains('automatic-discount-promo-modal__close')) closePromotionPicker();
      });
    });
    root.querySelectorAll('[data-promo-variant-id]').forEach(button => {
      button.addEventListener('click', () => addPromotionVariant(button.getAttribute('data-promo-variant-id'), button));
    });
  }

  function getCurrencyPreference(element) {
    const attr = element.getAttribute('data-currency-code-enabled');
    if (attr === 'true') return true;
    if (attr === 'false') return false;
    return !!state.currencyCodeEnabled;
  }

  function formatMoney(cents, useCurrencyCode) {
    const format = useCurrencyCode ? state.moneyWithCurrencyFormat : state.moneyFormat;

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(Math.round(cents), format);
    }

    const currency = window.Shopify?.currency?.active || 'CAD';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-CA', {
        style: 'currency',
        currency,
        currencyDisplay: useCurrencyCode ? 'code' : 'symbol'
      }).format(cents / 100);
    } catch (error) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  function htmlEscape(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function getDiscountText(discount, element) {
    const value = readDiscountValue(discount);

    if (value.type === 'percentage') {
      return `${Math.round(value.amount * 100)}% off`;
    }

    if (value.type === 'fixed_amount') {
      return `${formatMoney(value.amount, getCurrencyPreference(element))} off`;
    }

    return 'a promo discount';
  }

  function getTargetDiscounts(element) {
    const mode = element.getAttribute('data-automatic-discount-mode');
    const now = Date.now();

    if (mode === 'product-card') {
      const variants = getProductVariants(element.getAttribute('data-product-id'));
      const availableVariants = variants.filter(variant => variant.available !== false);
      const candidates = availableVariants.length ? availableVariants : variants;
      const activeCandidates = candidates.flatMap(variant => getActiveDiscountCandidates(variant, now, true));

      const minimumCandidates = activeCandidates.filter(candidate => candidate.minimumRequirement);
      return {
        immediate: pickLowestPrice(activeCandidates.filter(candidate => !candidate.minimumRequirement)),
        minimum: pickBestBySavings(minimumCandidates.filter(candidate => candidate.minimumRequirement?.type !== 'subtotal')),
        subtotalTiers: getSubtotalTiers(minimumCandidates)
      };
    }

    const variant = getVariant(element.getAttribute('data-variant-id'));
    const activeCandidates = getActiveDiscountCandidates(variant, now, mode === 'product-form');
    const upcomingCandidates = getUpcomingDiscountCandidates(variant, now);

    const minimumCandidates = activeCandidates.filter(candidate => candidate.minimumRequirement);
    return {
      immediate: pickBestBySavings(activeCandidates.filter(candidate => !candidate.minimumRequirement)),
      minimum: pickBestBySavings(minimumCandidates.filter(candidate => candidate.minimumRequirement?.type !== 'subtotal')),
      subtotalTiers: getSubtotalTiers(minimumCandidates),
      upcoming: upcomingCandidates[0] || null,
      endingSoon: activeCandidates.filter(candidate => candidate.endsAt && candidate.endsAt.getTime() > now && candidate.endsAt.getTime() - now <= 24 * 60 * 60 * 1000).sort((a, b) => a.endsAt.getTime() - b.endsAt.getTime())[0] || null
    };
  }

  function getSubtotalTiers(candidates) {
    const tiers = new Map();

    for (const candidate of candidates) {
      if (candidate.minimumRequirement?.type !== 'subtotal') continue;
      const discountId = String(candidate.discount?.id || candidate.discount?.numericId || '');
      const key = `${discountId}:${candidate.minimumRequirement.amountCents}`;
      const existing = tiers.get(key);
      if (!existing || candidate.savingsScore > existing.savingsScore) tiers.set(key, candidate);
    }

    return [...tiers.values()].sort((a, b) => {
      const thresholdDifference = a.minimumRequirement.amountCents - b.minimumRequirement.amountCents;
      if (thresholdDifference) return thresholdDifference;
      return a.savingsScore - b.savingsScore;
    });
  }

  function shouldShowMinimumPromo(element, minimumCandidate, immediateCandidate) {
    if (!minimumCandidate) return false;

    if (immediateCandidate && minimumCandidate.savingsScore <= immediateCandidate.savingsScore) {
      return false;
    }

    const mode = element.getAttribute('data-automatic-discount-mode');
    const requirement = minimumCandidate.minimumRequirement;

    if (mode === 'cart-line' || mode === 'cart-line-total') {
      const quantity = getCartPromotionQuantity(minimumCandidate, element);

      if (requirement?.type === 'quantity' && quantity >= requirement.quantity) {
        return false;
      }

      if (requirement?.type === 'subtotal' && element.getAttribute('data-line-has-discount') === 'true') {
        return false;
      }
    }

    return true;
  }

  function getPriceClassNames(element) {
    const mode = element.getAttribute('data-automatic-discount-mode');

    if (mode === 'cart-line' || mode === 'cart-line-total') {
      return {
        price: 'line-item__price',
        highlight: 'line-item__price line-item__price--highlight',
        compare: 'line-item__price line-item__price--compare'
      };
    }

    return {
      price: 'price',
      highlight: 'price price--highlight',
      compare: 'price price--compare'
    };
  }

  function buildRegularPriceHtml(variant, element) {
    const classes = getPriceClassNames(element);
    const useCurrencyCode = getCurrencyPreference(element);
    const price = variant ? variant.price : 0;
    const compareAtPrice = variant ? variant.compareAtPrice : 0;

    if (compareAtPrice > price) {
      return `
        <span class="${classes.highlight}">
          <span class="visually-hidden">Sale price</span>
          ${formatMoney(price, useCurrencyCode)}
        </span>
        <span class="${classes.compare}">
          <span class="visually-hidden">Regular price</span>
          ${formatMoney(compareAtPrice, useCurrencyCode)}
        </span>
      `;
    }

    return `
      <span class="${classes.price}">
        <span class="visually-hidden">Sale price</span>
        ${formatMoney(price, useCurrencyCode)}
      </span>
    `;
  }

  function hasCustomerPricing(candidate) {
    const discount = candidate?.discount;
    const eligibilityType = String(discount?.eligibility?.type || 'all');
    return state.customerDiscount > 0 && eligibilityType !== 'all' &&
      discontinuedPercentage(discount?.percent) === state.customerDiscount;
  }

  function buildImmediatePriceHtml(candidate, element) {
    const classes = getPriceClassNames(element);
    const useCurrencyCode = getCurrencyPreference(element);
    const variant = candidate.variant;
    const regularPrice = candidate.discount?.regularPriceOnly === true && variant.compareAtPrice > variant.price ? variant.compareAtPrice : variant.price || 0;
    const discountedPrice = candidate.discountedPrice;
    const prefix = element.getAttribute('data-automatic-discount-mode') === 'product-card' &&
      element.getAttribute('data-product-price-varies') === 'true'
        ? 'From '
        : '';

    if (hasCustomerPricing(candidate)) {
      const label = element.getAttribute('data-automatic-discount-mode') === 'product-form' ? '' : '<span class="automatic-discount-your-price__label">Your price:</span>';
      return `
        <span class="${classes.price} automatic-discount-your-price">${label}${htmlEscape(prefix)}${formatMoney(discountedPrice, useCurrencyCode)}</span>
        <span class="${classes.price} automatic-discount-regular-price">Regular price ${formatMoney(regularPrice, useCurrencyCode)}</span>
      `;
    }

    return `
      <span class="${classes.highlight}">
        <span class="visually-hidden">Sale price</span>
        ${htmlEscape(prefix)}${formatMoney(discountedPrice, useCurrencyCode)}
      </span>
      <span class="${classes.compare}">
        <span class="visually-hidden">Regular price</span>
        ${formatMoney(regularPrice, useCurrencyCode)}
      </span>
    `;
  }

  function buildLineTotalHtml(candidate, element) {
    const useCurrencyCode = getCurrencyPreference(element);
    const quantity = Math.max(1, Number(element.getAttribute('data-quantity') || 1));
    const regularLinePrice = (candidate.discount?.regularPriceOnly === true && candidate.variant.compareAtPrice > candidate.variant.price ? candidate.variant.compareAtPrice : candidate.variant.price || 0) * quantity;
    const discountedLinePrice = candidate.discountedPrice * quantity;

    if (hasCustomerPricing(candidate)) {
      return `
        <span class="line-item__price automatic-discount-your-price"><span class="automatic-discount-your-price__label">Your price:</span>${formatMoney(discountedLinePrice, useCurrencyCode)}</span>
        <span class="line-item__price automatic-discount-regular-price">Regular price ${formatMoney(regularLinePrice, useCurrencyCode)}</span>
      `;
    }

    return `
      <span class="line-item__price line-item__price--highlight">
        ${formatMoney(discountedLinePrice, useCurrencyCode)}
      </span>
      <span class="line-item__price line-item__price--compare">
        ${formatMoney(regularLinePrice, useCurrencyCode)}
      </span>
    `;
  }

  function ensureOriginalHtml(element) {
    if (!originalHtmlByElement.has(element)) {
      originalHtmlByElement.set(element, element.innerHTML);
    }
  }

  function restoreOriginalHtml(element) {
    if (originalHtmlByElement.has(element)) {
      element.innerHTML = originalHtmlByElement.get(element);
    }

    element.classList.remove('automatic-discount-price-list--active', 'automatic-discount-customer-price');
    element.classList.remove('automatic-discount-message-list--active');
  }

  function resetPriceElement(element) {
    const mode = element.getAttribute('data-automatic-discount-mode');

    if (mode === 'product-form') {
      const variant = getVariant(element.getAttribute('data-variant-id'));
      if (variant) {
        element.innerHTML = buildRegularPriceHtml(variant, element);
      } else {
        restoreOriginalHtml(element);
      }
    } else {
      restoreOriginalHtml(element);
    }

    element.querySelectorAll(MESSAGE_SELECTOR).forEach(message => message.remove());
  }

  function buildMessageText(element, candidate) {
    const discountText = getDiscountText(candidate.discount, element);
    const requirement = candidate.minimumRequirement;
    const mode = element.getAttribute('data-automatic-discount-mode');
    const quantity = getCartPromotionQuantity(candidate, element);

    if (mode === 'cart-line' || mode === 'cart-line-total') {
      if (requirement?.type === 'quantity' && quantity < requirement.quantity) {
        const needed = requirement.quantity - quantity;
        return `Add ${needed} more to unlock ${discountText} automatically.`;
      }

      if (requirement?.type === 'subtotal') {
        return `Spend ${formatMoney(requirement.amountCents, getCurrencyPreference(element))}+ to unlock ${discountText} automatically.`;
      }

      return `Save ${discountText} automatically when promo requirements are met.`;
    }

    if (requirement?.type === 'quantity') {
      return `Buy ${requirement.quantity}+ and save ${discountText} automatically.`;
    }

    if (requirement?.type === 'subtotal') {
      return `Spend ${formatMoney(requirement.amountCents, getCurrencyPreference(element))}+ and save ${discountText} automatically.`;
    }

    return `Save ${discountText} automatically when promo requirements are met.`;
  }

  function appendImmediateProductPromo(element, candidate) {
    if (!['product-form', 'product-card', 'variant-card'].includes(element.getAttribute('data-automatic-discount-mode')) || !candidate || hasCustomerPricing(candidate)) return;
    const title = String(candidate.discount?.title || 'Automatic promotion').trim();
    const discountText = getDiscountText(candidate.discount, element);
    const isMetafieldDiscount = String(candidate.discount?.id || '').startsWith('metafield-discount-');
    const isExtraBundleDiscount = candidate.variant?.bundleProduct === true && !isMetafieldDiscount;
    const displayText = isMetafieldDiscount
      ? `<strong>${htmlEscape(discountText.replace(/ off$/i, ''))} ${htmlEscape(title)}</strong>`
      : `<strong>${htmlEscape(title)}</strong> <span aria-hidden="true">&middot;</span> ${isExtraBundleDiscount ? 'Extra ' : ''}${htmlEscape(discountText)}${isExtraBundleDiscount ? ' on top of bundle savings' : ''}.`;

    element.insertAdjacentHTML('beforeend', `
      <div class="automatic-discount-message automatic-discount-message--active-promo" role="note">
        <span class="automatic-discount-message__badge">Promo</span>
        <span class="automatic-discount-message__text">${displayText}</span>
      </div>
    `);

    element.classList.add('automatic-discount-message-list--active');
  }

  function appendUpcomingProductPromo(element, candidate) {
    if (element.getAttribute('data-automatic-discount-mode') !== 'product-form' || !candidate?.startsAt) return;
    const title = String(candidate.discount?.title || 'Upcoming sale').trim();
    const discountText = getDiscountText(candidate.discount, element);
    const startText = candidate.startsAt.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
    element.insertAdjacentHTML('beforeend', `
      <div class="automatic-discount-message automatic-discount-message--upcoming-promo" role="note">
        <span class="automatic-discount-message__badge">Upcoming</span>
        <span class="automatic-discount-message__text"><strong>${htmlEscape(title)}</strong> <span aria-hidden="true">&middot;</span> ${htmlEscape(discountText)} starts ${htmlEscape(startText)}.</span>
      </div>
    `);
    element.classList.add('automatic-discount-message-list--active');
  }
  function appendEndingCountdown(element, candidate) {
    if (element.getAttribute('data-automatic-discount-mode') !== 'product-form' || !candidate?.endsAt) return;
    element.insertAdjacentHTML('beforeend', `
      <div class="automatic-discount-message automatic-discount-message--ending-promo" role="timer" data-promo-ends-at="${candidate.endsAt.getTime()}">
        <span class="automatic-discount-message__badge">Ends in</span>
        <strong class="automatic-discount-countdown">--:--:--</strong>
      </div>
    `);
    element.classList.add('automatic-discount-message-list--active');
  }

  let discountCountdownTimer = null;
  function updateDiscountCountdowns() {
    let expired = false;
    document.querySelectorAll('[data-promo-ends-at]').forEach(element => {
      const remaining = Number(element.getAttribute('data-promo-ends-at')) - Date.now();
      if (remaining <= 0) { expired = true; return; }
      const seconds = Math.max(0, Math.floor(remaining / 1000));
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;
      const output = element.querySelector('.automatic-discount-countdown');
      if (output) output.textContent = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    });
    if (expired) scheduleRender(document);
  }

  function scheduleDiscountCountdowns() {
    window.clearInterval(discountCountdownTimer);
    if (!document.querySelector('[data-promo-ends-at]')) return;
    updateDiscountCountdowns();
    discountCountdownTimer = window.setInterval(updateDiscountCountdowns, 1000);
  }
  function appendMinimumMessage(element, candidate) {
    const messageText = buildMessageText(element, candidate);
    const discountTitle = candidate.discount.title ? ` title="${htmlEscape(candidate.discount.title)}"` : '';
    const action = candidate.minimumRequirement?.type === 'quantity'
      ? '<button type="button" class="automatic-discount-message__action" data-promo-open>View eligible products</button>'
      : '';

    element.insertAdjacentHTML('beforeend', `
      <div class="automatic-discount-message" role="note"${discountTitle}>
        <span class="automatic-discount-message__badge">Promo</span>
        <span class="automatic-discount-message__text">${htmlEscape(messageText)}</span>
        ${action}
      </div>
    `);

    element.classList.add('automatic-discount-message-list--active');
  }

  function appendSubtotalTiers(element, tiers) {
    if (!Array.isArray(tiers) || tiers.length === 0) return;
    const useCurrencyCode = getCurrencyPreference(element);
    const tierRows = tiers.map(candidate => {
      const spend = formatMoney(candidate.minimumRequirement.amountCents, useCurrencyCode);
      const savings = getDiscountText(candidate.discount, element);
      return `<span class="automatic-discount-tier"><span>Spend ${htmlEscape(spend)}+</span><strong>Save ${htmlEscape(savings.replace(/\s+off$/i, ''))}</strong></span>`;
    }).join('');

    element.insertAdjacentHTML('beforeend', `
      <div class="automatic-discount-message automatic-discount-message--tiers" role="note" aria-label="Automatic order discount tiers">
        <span class="automatic-discount-message__badge">Order offers</span>
        <span class="automatic-discount-tier-list">${tierRows}</span>
        <span class="automatic-discount-tier-note">Best qualifying offer applies automatically at checkout. Offers do not stack.</span>
      </div>
    `);

    element.classList.add('automatic-discount-message-list--active');
  }

  function renderDiscountedUnitPrice(priceElement, immediate) {
    const unit = priceElement.parentElement?.querySelector('[data-automatic-discount-unit-price]');
    if (!unit) return;

    if (!unit.dataset.originalHtml) unit.dataset.originalHtml = unit.innerHTML;
    unit.innerHTML = unit.dataset.originalHtml;

    const variantId = unit.getAttribute('data-variant-id');
    const variant = getVariant(variantId);
    const baseUnitPrice = Number(unit.getAttribute('data-unit-price-cents'));
    if (!immediate || !variant || String(immediate.variant?.id) !== String(variantId) ||
        !Number.isFinite(baseUnitPrice) || baseUnitPrice <= 0 || variant.price <= 0) return;

    const discountedUnitPrice = Math.round(baseUnitPrice * immediate.discountedPrice / variant.price);
    const baseMoney = formatMoney(baseUnitPrice, false);
    const discountedMoney = formatMoney(discountedUnitPrice, false);
    if (unit.innerHTML.includes(baseMoney)) {
      unit.innerHTML = unit.innerHTML.replace(baseMoney, discountedMoney);
      return;
    }

    // Keep Shopify's localized measurement label when its money formatter differs.
    const baseNumber = (baseUnitPrice / 100).toFixed(2);
    if (unit.innerHTML.includes(baseNumber)) {
      unit.innerHTML = unit.innerHTML.replace(baseNumber, (discountedUnitPrice / 100).toFixed(2));
    }
  }
  function renderPriceElement(element) {
    ensureOriginalHtml(element);
    resetPriceElement(element);

    const target = getTargetDiscounts(element);
    let immediate = target.immediate;
    const minimum = target.minimum;
    const subtotalTiers = target.subtotalTiers || [];
    const upcoming = target.upcoming;
    const endingSoon = target.endingSoon;
    const lineAlreadyDiscounted = element.getAttribute('data-line-has-discount') === 'true';
    if (element.getAttribute('data-automatic-discount-mode') === 'product-form') {
      const priceLabel = element.closest('.product-form__info-item')?.querySelector('.product-form__info-title');
      if (priceLabel) {
        if (!priceLabel.dataset.originalPriceLabel) priceLabel.dataset.originalPriceLabel = priceLabel.textContent;
        priceLabel.textContent = hasCustomerPricing(immediate) && !lineAlreadyDiscounted ? 'Your price:' : priceLabel.dataset.originalPriceLabel;
      }
    }

    if (immediate && !lineAlreadyDiscounted) {
      element.innerHTML = buildImmediatePriceHtml(immediate, element);
      element.classList.add('automatic-discount-price-list--active');
      element.classList.toggle('automatic-discount-customer-price', hasCustomerPricing(immediate));
      appendImmediateProductPromo(element, immediate);
    }

    if (!lineAlreadyDiscounted) appendSubtotalTiers(element, subtotalTiers);

    if (shouldShowMinimumPromo(element, minimum, immediate)) {
      appendMinimumMessage(element, minimum);
    }

    if (upcoming) appendUpcomingProductPromo(element, upcoming);
    if (endingSoon) appendEndingCountdown(element, endingSoon);
    renderDiscountedUnitPrice(element, immediate);
  }

  function renderLineTotalElement(element) {
    ensureOriginalHtml(element);
    restoreOriginalHtml(element);

    if (element.getAttribute('data-line-has-discount') === 'true') return;

    const target = getTargetDiscounts(element);
    const immediate = target.immediate;

    if (!immediate) return;

    element.innerHTML = buildLineTotalHtml(immediate, element);
    element.classList.add('automatic-discount-price-list--active');
    element.classList.toggle('automatic-discount-customer-price', hasCustomerPricing(immediate));
  }

  function renderTarget(element) {
    if (element.matches(LINE_TOTAL_SELECTOR)) {
      renderLineTotalElement(element);
      return;
    }

    renderPriceElement(element);
  }

  function renderBundleSavings(root) {
    (root || document).querySelectorAll('[data-bundle-savings]').forEach(summary => {
      const productId = summary.getAttribute('data-product-id');
      const priceElement = Array.from(document.querySelectorAll(`${PRICE_SELECTOR}[data-automatic-discount-mode="product-form"]`))
        .find(element => element.getAttribute('data-product-id') === productId);
      if (!priceElement) return;

      const variantId = priceElement.getAttribute('data-variant-id');
      const variant = getVariant(variantId);
      if (!variant) return;

      const mapElement = summary.parentElement.querySelector('[data-bundle-savings-map]');
      const regularByVariant = parseJsonSafely(mapElement?.textContent || '{}', {});
      const componentTotal = Number(regularByVariant[String(variantId)] || 0);
      const regularTotal = Math.max(componentTotal, Number(variant.compareAtPrice || 0), Number(variant.price || 0));
      const bundlePrice = Number(variant.price || 0);
      const immediate = getTargetDiscounts(priceElement).immediate;
      const extraPrice = immediate && Number(immediate.discountedPrice) < bundlePrice
        ? Math.max(0, Number(immediate.discountedPrice)) : bundlePrice;
      const extraSavings = Math.max(0, bundlePrice - extraPrice);
      const savings = Math.max(0, regularTotal - extraPrice);
      summary.hidden = !(componentTotal > 0 && savings > 0);
      summary.setAttribute('data-variant-id', String(variantId));
      if (summary.hidden) return;

      const money = cents => formatMoney(cents, getCurrencyPreference(priceElement));
      summary.querySelector('[data-bundle-regular]').textContent = money(regularTotal);
      summary.querySelector('[data-bundle-price]').textContent = money(bundlePrice);
      const baseRow = summary.querySelector('[data-bundle-base-row]');
      baseRow.hidden = extraSavings <= 0 || regularTotal <= bundlePrice;
      if (!baseRow.hidden) summary.querySelector('[data-bundle-base-amount]').textContent = `-${money(regularTotal - bundlePrice)}`;
      const extraRow = summary.querySelector('[data-bundle-extra-row]');
      const finalRow = summary.querySelector('[data-bundle-final-row]');
      extraRow.hidden = finalRow.hidden = extraSavings <= 0;
      if (extraSavings > 0) {
        summary.querySelector('[data-bundle-extra-label]').textContent = `Extra ${getDiscountText(immediate.discount, priceElement)}`;
        summary.querySelector('[data-bundle-extra-amount]').textContent = `-${money(extraSavings)}`;
        summary.querySelector('[data-bundle-final-price]').textContent = money(extraPrice);
      }
      summary.querySelector('[data-bundle-saved]').textContent =
        `${money(savings)} (${Math.round(savings * 100 / regularTotal)}%)`;

    });
  }
  function renderAll(root) {
    collectMaps(root || document);
    (root || document).querySelectorAll(TARGET_SELECTOR).forEach(renderTarget);
    renderBundleSavings(root || document);
    scheduleNextDiscountWindowRender();
    scheduleDiscountCountdowns();
  }

  let renderTimer = null;
  function scheduleRender(root) {
    window.clearTimeout(renderTimer);
    renderTimer = window.setTimeout(() => renderAll(root || document), 0);
  }

  document.addEventListener('click', async function (event) {
    const trigger = event.target.closest?.('[data-promo-open]');
    if (!trigger) return;

    event.preventDefault();
    const element = trigger.closest(PRICE_SELECTOR);
    if (!element) return;

    const candidate = getTargetDiscounts(element).minimum;
    const requirement = candidate?.minimumRequirement;
    if (!candidate || requirement?.type !== 'quantity') return;

    const quantity = element.getAttribute('data-automatic-discount-mode') === 'cart-line' ||
      element.getAttribute('data-automatic-discount-mode') === 'cart-line-total'
      ? getCartPromotionQuantity(candidate, element)
      : await getLiveCartPromotionQuantity(candidate);
    const needed = requirement.quantity - quantity;
    if (needed > 0) openPromotionPicker(candidate, needed);
  });

  document.addEventListener('variant:changed', function (event) {
    const variant = event.detail && event.detail.variant;

    if (variant && variant.id) {
      const selectedVariant = getVariant(variant.id);

      document.querySelectorAll(`${PRICE_SELECTOR}[data-automatic-discount-mode="product-form"]`).forEach(element => {
        if (!selectedVariant) return;

        const elementProductId = element.getAttribute('data-product-id');
        if (elementProductId && selectedVariant.productId && String(elementProductId) !== String(selectedVariant.productId)) return;

        element.setAttribute('data-variant-id', String(variant.id));
      });
    }

    scheduleRender();
    window.setTimeout(() => renderAll(document), 80);
  });

  document.addEventListener('DOMContentLoaded', function () {
    renderAll(document);
  });

  document.documentElement.addEventListener('cart:refresh', function () {
    scheduleRender();
  });

  document.addEventListener('shopify:section:load', function (event) {
    renderAll(event.target || document);
  });

  const observer = new MutationObserver(mutations => {
    let shouldRender = false;

    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => {
        if (node.nodeType !== 1) return;

        if (
          node.matches?.(MAP_SELECTOR) ||
          node.matches?.(TARGET_SELECTOR) ||
          node.querySelector?.(MAP_SELECTOR) ||
          node.querySelector?.(TARGET_SELECTOR)
        ) {
          shouldRender = true;
        }
      });
    });

    if (shouldRender) scheduleRender();
  });

  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      observer.observe(document.body, { childList: true, subtree: true });
    });
  }

  renderAll(document);
})();

